import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { ApiService } from 'projects/admin/src/app/services/api.service';
import { UiService } from 'projects/admin/src/app/services/ui.service';
import { FormHelper } from 'projects/admin/src/app/shared/form-helper';
import { BehavioranchorModel } from 'projects/core/src/lib/models/behavioranchor.model';
import { CompetenceModel } from 'projects/core/src/lib/models/competence.model';
import { ExerciseModel } from 'projects/core/src/lib/models/exercise.model';
import { ExercisepartModel } from 'projects/core/src/lib/models/exercisepart.model';
import {
  faPlus,
  faTrash
} from '@fortawesome/pro-light-svg-icons';
import { TranslocoService } from '@ngneat/transloco';
import { AuthStateService } from 'projects/admin/src/app/services/auth-state.service';

@Component({
  selector: 'app-exercise-competences',
  templateUrl: './exercise-competences.component.html',
  styleUrls: ['./exercise-competences.component.scss']
})
export class ExerciseCompetencesComponent implements OnInit, OnChanges {

  @Input() exercise: ExerciseModel = null;
  @Input('competences') all: Array<CompetenceModel> = []; // tslint:disable-line:no-input-rename
  @Output() exerciseChange = new EventEmitter<ExerciseModel>();
  @Output() setEdit = new EventEmitter<string>();

  public inEdit = false;
  public competenceBlocks: Array<any> = [];
  public competencesFound: boolean;
  public editBlock: any = null;
  public editEntry: any = null;
  public submitting = false;
  public optionsCompetences: Array<any> = [];
  public items: Array<MenuItem> = [];

  public saveInheritVisible = false;
  public saveInheritBlock: any = null;
  public saveInheritEntry: any = null;
  public deleteInheritVisible = false;
  public deleteInheritBlock: any = null;
  public deleteInheritEntry: any = null;

  public form: UntypedFormGroup = this.fb.group({
    competence: new UntypedFormControl(null, [Validators.required]),
    behavioranchors: this.fb.array([])
  });

  public faPlus = faPlus;
  public faTrash = faTrash;

  constructor(
    private api: ApiService,
    private fb: UntypedFormBuilder,
    private ui: UiService,
    private auth: AuthStateService,
    private confirmation: ConfirmationService,
    public transloco: TranslocoService
  ) { }

  ngOnInit(): void {
    this.ui.setFocus();
  }

  ngOnChanges(): void {
    this.buildCompetences();
  }

  onNew(block: any): void {
    if (this.auth.hasRight('admin.exercise.edit')) {
      this.editBlock = block;
      this.editEntry = null;
      this.startEdit();
    }
  }

  onEdit(block: any, entry: any): void {
    if (this.auth.hasRight('admin.exercise.edit')) {
      this.editBlock = block;
      this.editEntry = entry;
      this.startEdit();
    }
  }

  onDelete(block: any, entry: any): void {
    if (this.auth.hasRight('admin.exercise.edit') && block && entry) {
      this.confirmation.confirm({
        message: this.transloco.translate('exercise_competences.really_delete', { value: entry.competence.getLang('name', this.transloco.getActiveLang())}),
        header: this.transloco.translate('common.really_delete'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.deleteInheritBlock = block;
          this.deleteInheritEntry = entry;
          if (this.exercise?.shouldShowInheritDialog()) {
            this.deleteInheritVisible = true;
          } else {
            this.deleteCompetence(false);
          }
        }
      });
    }
  }

  onCancel(): void {
    this.stopEdit();
    this.ui.hideMessage();
  }

  onSubmit(): void {
    if (this.exercise !== null) {
      this.ui.hideMessage();
      if (this.form.valid === true && this.editBlock !== null) {
        this.saveInheritBlock = this.editBlock;
        this.saveInheritEntry = this.editEntry;
        if (this.exercise?.shouldShowInheritDialog()) {
          this.saveInheritVisible = true;
        } else {
          this.saveCompetence(false);
        }
      } else {
        this.ui.showMessage('error', this.transloco.translate('common.error_on_form_please_correct'), this.transloco.translate('common.error'));
        FormHelper.markFormAsDirty(this.form);
      }
    }
  }

  startEdit(): void {
    this.inEdit = true;
    this.setupForm();
    this.setEdit.emit('competences');
    this.ui.setFormFocus();
  }

  stopEdit(): void {
    this.inEdit = false;
    this.clearForm();
    this.setEdit.emit(null);
    this.ui.setFocus();
  }

  public deleteCompetence(inherit: boolean = false): void {
    this.api.deleteExercisepartCompetence(this.deleteInheritBlock?.exercisepart?.id, this.deleteInheritEntry?.competence?.id, inherit).subscribe({
      next: (result: any) => {
        this.ui.toastSuccess(this.transloco.translate('exercise_competences.deleted', { value: this.deleteInheritEntry.competence.getLang('name', this.transloco.getActiveLang()) }), this.transloco.translate('common.deleted'));
        this.exercise = new ExerciseModel().deserialize(result.data);
        this.exerciseChange.emit(this.exercise);
        this.buildCompetences();
      },
      error: (error: any) => {
        this.ui.toastError(this.transloco.translate(error.message));
      }
    });
  }

  public saveCompetence(inherit: boolean = false): void {
    this.submitting = true;
    const payload = {
      hashed_id: this.exercise.hashed_id,
      exercisepart: this.saveInheritBlock.exercisepart.id,
      competence: (this.saveInheritEntry !== null) ? this.saveInheritEntry.competence.id : this.form.get('competence').value.id,
      behavioranchors: this.form.get('behavioranchors').value,
      inherit
    };
    this.api.saveExerciseCompetences(this.exercise.id, payload).subscribe({
      next: (result: any) => {
        this.exercise = new ExerciseModel().deserialize(result.data);
        this.exerciseChange.emit(this.exercise);
        this.stopEdit();
        this.buildCompetences();
      },
      error: (error: any) => {
        this.ui.showMessage('error', this.transloco.translate(error.message), this.transloco.translate('common.error'));
      }
    }).add(() => {
      this.submitting = false;
    });

  }

  private buildCompetences(): void {
    this.competencesFound = false;
    if (this.exercise !== null) {
      this.competenceBlocks = [];
      this.exercise.exerciseparts.forEach((exercisepart: ExercisepartModel) => {
        if (exercisepart.type === 'normal' &&  exercisepart.rating_start !== 'none') {
          const entries = [];
          if (exercisepart.behavioranchors.length > 0) {
            this.competencesFound = true;
            exercisepart.behavioranchors.forEach((behavioranchor: BehavioranchorModel) => {
              let entry = entries.find((actual: any) => actual.competence?.id === behavioranchor.competence_id);
              if (entry === undefined) {
                const competence = this.all.find((actual: CompetenceModel) => actual.id === behavioranchor.competence_id);
                entry = {
                  competence,
                  behavioranchors: []
                };
                entries.push(entry);
              }
              entry.behavioranchors.push(behavioranchor);
            });
          }
          entries.sort((a: any, b: any) => (
            a.competence?.position > b.competence?.position) ? 1 : ((b.competence?.position > a.competence?.position) ? -1 : 0)
          );
          this.competenceBlocks.push({
            exercisepart,
            entries
          });
        }
      });
    }
    this.buildAddMenu();
  }

  private buildAddMenu(): void {
    this.items = [];
    this.competenceBlocks.forEach((block: any) => {
      this.items.push({
        label: 'Für: ' + block.exercisepart.name,
        disabled: (block.entries.length === this.all.length),
        // icon: 'pi pi-trash',
        command: () => {
          this.onNew(block);
        }
      });
    });
  }

  private setupForm(): void {
    this.clearForm();
    this.optionsCompetences = [];
    if (this.editEntry !== null) {
      this.optionsCompetences = [this.editEntry.competence];
      this.form.get('competence').setValue(this.editEntry.competence);
      this.editEntry.behavioranchors.forEach((behavioranchor: BehavioranchorModel) => {
        this.addBehavioranchorEntry(behavioranchor.content, behavioranchor.expression_positive, behavioranchor.expression_negative);
      });
    } else if (this.editBlock !== null) {
      this.all.forEach((competence: CompetenceModel) => {
        if (this.editBlock.entries.find((actual: any) => actual.competence.id === competence.id) === undefined) {
          this.optionsCompetences.push(competence);
        }
      });
      this.addBehavioranchorEntry();
    }
  }

  private clearForm(): void {
    this.form.reset();
    this.optionsCompetences = [];
    this.form.patchValue({
      competence: null,
    });
    this.clearBehavioranchorEntries();
  }

  // input handling

  private clearBehavioranchorEntries(): void {
    const entries = this.form.get('behavioranchors') as UntypedFormArray;
    while (entries.length > 0) {
      entries.removeAt(0);
    }
  }

  public addBehavioranchorEntry(content: string = null, expression_positive: string = null, expression_negative: string = null): void {
    const entries = this.form.get('behavioranchors') as UntypedFormArray;
    entries.push(this.createBehavioranchorEntry(content, expression_positive, expression_negative));
  }

  public deleteBehavioranchorEntry(index: number): void {
    const entries = this.form.get('behavioranchors') as UntypedFormArray;
    if (entries.length > 0) {
      entries.removeAt(index);
    }
  }

  private createBehavioranchorEntry(content: string = null, expression_positive: string = null, expression_negative: string = null): UntypedFormGroup {
    return this.fb.group({
      content: new UntypedFormControl(content, [Validators.required]),
      expression_positive: new UntypedFormControl(expression_positive),
      expression_negative: new UntypedFormControl(expression_negative),
    });
  }

}
